import { ref } from 'vue'
import { useNotifications } from '@/composables/useNotifications'
import { env } from '@/utils/env'
import { logError } from '@/utils/errors'

interface GoogleUser {
  credential: string
  select_by: string
}

interface GoogleAuthConfig {
  client_id: string
  callback: (response: GoogleUser) => void
  auto_select?: boolean
  cancel_on_tap_outside?: boolean
  context?: 'signin' | 'signup' | 'use'
  ux_mode?: 'popup' | 'redirect'
  use_fedcm_for_prompt?: boolean
}

const isLoading = ref(false)
const isInitialized = ref(false)
const googleCredential = ref<string | null>(null)
let initializationPromise: Promise<void> | null = null
let promptActive = false
let promptCooldownUntil = 0
const ONE_TAP_ATTEMPT_KEY = 'walabi_google_one_tap_attempted'

export const useGoogleAuth = () => {
  const { showNotification } = useNotifications()

  // Google Client ID desde variables de entorno
  const GOOGLE_CLIENT_ID = env.GOOGLE_CLIENT_ID

  const initializeGoogleAuth = (options?: { silent?: boolean }): Promise<void> => {
    const silent = options?.silent ?? false

    if (isInitialized.value) {
      return Promise.resolve()
    }

    if (initializationPromise) {
      return initializationPromise
    }

    initializationPromise = new Promise((resolve, reject) => {
      if (typeof window.google === 'undefined') {
        const checkGoogle = setInterval(() => {
          if (typeof window.google !== 'undefined') {
            clearInterval(checkGoogle)
            setupGoogleAuth(silent)
            resolve()
          }
        }, 100)

        setTimeout(() => {
          clearInterval(checkGoogle)
          initializationPromise = null
          reject(new Error('Google Identity Services no se pudo cargar'))
        }, 10000)
      } else {
        setupGoogleAuth(silent)
        resolve()
      }
    }).finally(() => {
      if (isInitialized.value) {
        initializationPromise = Promise.resolve()
      } else {
        initializationPromise = null
      }
    })

    return initializationPromise
  }

  const setupGoogleAuth = (silent = false) => {
    try {
      if (!GOOGLE_CLIENT_ID) {
        throw new Error('Google Client ID no configurado')
      }
      
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: handleGoogleResponse,
        auto_select: false,
        cancel_on_tap_outside: true,
        use_fedcm_for_prompt: false,
      })
      isInitialized.value = true
    } catch (error) {
      logError('Error inicializando Google Auth', error)
      if (!silent) {
        showNotification('Error al inicializar Google Auth', 'error')
      }
    }
  }

  const handleGoogleResponse = (response: GoogleUser) => {
    if (response.credential) {
      googleCredential.value = response.credential
    } else {
      logError('No se recibió credencial de Google', new Error('Missing Google credential'))
      showNotification('Error al obtener credencial de Google', 'error')
    }
  }

  const renderGoogleButton = (element: HTMLElement, options?: any) => {
    if (!isInitialized.value) {
      initializeGoogleAuth({ silent: false }).then(() => {
        if (window.google && window.google.accounts && window.google.accounts.id) {
          window.google.accounts.id.renderButton(element, {
            theme: 'outline',
            size: 'large',
            type: 'standard',
            text: 'signin_with',
            shape: 'rectangular',
            logo_alignment: 'left',
            width: element.offsetWidth || 300,
            ...options
          })
        }
      }).catch(error => {
        logError('Error rendering Google button', error)
        showNotification('Error al cargar el botón de Google', 'error')
      })
    } else {
      if (window.google && window.google.accounts && window.google.accounts.id) {
        window.google.accounts.id.renderButton(element, {
          theme: 'outline',
          size: 'large',
          type: 'standard',
          text: 'signin_with',
          shape: 'rectangular',
          logo_alignment: 'left',
          width: element.offsetWidth || 300,
          ...options
        })
      }
    }
  }

  const promptOneTap = async () => {
    try {
      if (promptActive || Date.now() < promptCooldownUntil) {
        return
      }

      if (typeof window !== 'undefined' && window.sessionStorage.getItem(ONE_TAP_ATTEMPT_KEY) === '1') {
        return
      }

      await initializeGoogleAuth({ silent: true })

      if (window.google?.accounts?.id) {
        promptActive = true
        if (typeof window !== 'undefined') {
          window.sessionStorage.setItem(ONE_TAP_ATTEMPT_KEY, '1')
        }
        window.google.accounts.id.prompt()
        window.setTimeout(() => {
          promptActive = false
        }, 1500)
      }
    } catch (error) {
      promptActive = false
      promptCooldownUntil = Date.now() + 60_000
      logError('Error mostrando Google One Tap', error)
    }
  }

  const cancelOneTap = () => {
    try {
      if (window.google?.accounts?.id) {
        window.google.accounts.id.cancel()
      }
    } catch (error) {
      logError('Error cancelando Google One Tap', error)
    } finally {
      promptActive = false
    }
  }

  // Función para decodificar el JWT token de Google (opcional, para debug)
  const decodeGoogleToken = (token: string) => {
    try {
      const base64Url = token.split('.')[1]
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      )
      return JSON.parse(jsonPayload)
    } catch (error) {
      logError('Error decodificando token de Google', error)
      return null
    }
  }

  const resetCredential = () => {
    googleCredential.value = null
  }

  return {
    isLoading,
    isInitialized,
    googleCredential,
    initializeGoogleAuth,
    renderGoogleButton,
    promptOneTap,
    cancelOneTap,
    decodeGoogleToken,
    resetCredential,
  }
}

// Tipos para Google Identity Services
declare global {
  interface Window {
    google: {
      accounts: {
        id: {
          initialize: (config: GoogleAuthConfig) => void
          prompt: (callback?: (notification: any) => void) => void
          renderButton: (element: HTMLElement, options: any) => void
          disableAutoSelect: () => void
          storeCredential: (credential: any) => void
          cancel: () => void
          onGoogleLibraryLoad: () => void
          revoke: (email: string, callback: () => void) => void
        }
      }
    }
  }
}
